import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Hero from "../components/Hero";
import SpecialMoments from "../components/SpecialMoments";
import api from "../lib/api";
import SEO from "../lib/SEO";
import { useSettings } from "../lib/SettingsContext";

export default function Home() {
    const { settings } = useSettings();
    const [flavours, setFlavours] = useState([]);
    const [categories, setCategories] = useState([]);

    useEffect(() => {
        api.get("/flavours").then((r) => setFlavours(Array.isArray(r.data) ? r.data : [])).catch(() => {});
        api.get("/categories").then((r) => setCategories(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    }, []);

    const marqueeFlavours = flavours.length > 0 ? [...flavours, ...flavours] : [];
    const featuredCategories = categories.slice(0, 6);

    return (
        <main data-testid="home-page">
            <SEO
                title="Cake Central — Bespoke Cakes in Sydney"
                description="Sydney's bespoke custom cake studio. Wedding, birthday, baby shower & celebration cakes hand-crafted in NSW."
                image="https://images.unsplash.com/photo-1604702389032-28cff7432c48?w=1200"
                path="/"
            />

            <Hero />

            {/* About / Intro snippet — full-bleed band, contained text */}
            <section className="relative bg-[#FFF7FB] py-20 md:py-28">
                <div className="max-w-3xl mx-auto px-6 text-center">
                    <p className="text-xs tracking-[0.45em] uppercase text-blush mb-5 font-medium">A Sydney studio</p>
                    <h2 className="font-display text-3xl md:text-5xl lg:text-6xl text-ink mb-6 leading-[1.05]">
                        Cakes made with <span className="italic text-blush">intention</span>,<br className="hidden md:block" />
                        for <span className="italic text-blush">moments</span> that matter.
                    </h2>
                    <p className="text-base md:text-lg text-ink-soft leading-relaxed max-w-2xl mx-auto">
                        {settings.about_short ||
                            "We craft custom cakes for weddings, birthdays, baby showers, and every celebration in between — designed with you, made by hand, here in Sydney."}
                    </p>
                    <div className="mt-10 flex flex-wrap justify-center gap-4">
                        <Link to="/gallery" className="btn-pink" data-testid="home-shop-btn">
                            Browse our cakes <ArrowRight size={14} />
                        </Link>
                        <Link to="/about" className="btn-pink btn-pink-outline" data-testid="home-about-btn">
                            Our story
                        </Link>
                    </div>
                </div>
            </section>

            {/* Special Moments Gallery */}
            <SpecialMoments />

            {/* Featured Flavours — full-bleed auto-scrolling marquee */}
            <section className="bg-white border-y border-[#EAE3DE] py-16 md:py-20 overflow-hidden">
                <div className="flex flex-col items-center text-center mb-10 px-4">
                    <p className="text-xs tracking-[0.45em] uppercase text-blush mb-3">Our menu</p>
                    <h2 className="font-display text-3xl md:text-5xl text-ink">
                        Featured <span className="italic">flavours</span>
                    </h2>
                </div>

                {marqueeFlavours.length > 0 && (
                    <div className="relative w-full overflow-hidden">
                        {/* Fade edges */}
                        <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-24 md:w-40 bg-gradient-to-r from-blush-pale to-transparent z-10" />
                        <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-24 md:w-40 bg-gradient-to-l from-blush-pale to-transparent z-10" />

                        <div className="flex gap-5 md:gap-7 animate-marquee" style={{ width: "max-content" }}>
                            {marqueeFlavours.map((f, i) => (
                                <div
                                    key={`${f.id}-${i}`}
                                    className="flex-shrink-0 w-44 md:w-60 text-center"
                                    data-testid={i < flavours.length ? `home-flavour-${i}` : undefined}
                                >
                                    <div className="aspect-square overflow-hidden mb-4 bg-blush-pale flex items-center justify-center">
                                        {f.image_url ? (
                                            <img
                                                src={f.image_url}
                                                alt={f.name}
                                                className="w-full h-full object-cover hover:scale-110 transition-transform duration-700"
                                                loading="lazy"
                                            />
                                        ) : (
                                            <span className="text-[9px] tracking-[0.3em] uppercase text-blush/60">Image soon</span>
                                        )}
                                    </div>
                                    <p className="font-display text-base md:text-lg text-ink">{f.name}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <div className="text-center mt-10">
                    <Link
                        to="/flavours"
                        className="text-xs tracking-[0.3em] uppercase text-ink hover:text-blush transition-colors"
                        data-testid="home-flavours-all"
                    >
                        View all flavours →
                    </Link>
                </div>
            </section>

            {/* Shop by category — wide editorial grid */}
            <section className="bg-[#FFF7FB] py-20 md:py-28">
                <div className="px-4 sm:px-6 lg:px-10">
                    <div className="text-center mb-14 max-w-3xl mx-auto">
                        <p className="text-xs tracking-[0.45em] uppercase text-blush mb-3 font-medium">For every occasion</p>
                        <h2 className="font-display text-3xl md:text-5xl lg:text-6xl text-ink leading-[1.05]">
                            Shop by <span className="italic text-blush">category</span>
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 md:gap-3 max-w-screen-2xl mx-auto">
                        {featuredCategories.map((c, i) => (
                            <Link
                                key={c.id}
                                to={`/gallery/${c.slug}`}
                                className="group block relative overflow-hidden card-lift"
                                data-testid={`home-category-${i}`}
                            >
                                <div className="aspect-[4/5] overflow-hidden bg-blush-pale">
                                    {c.cover_image_url && (
                                        <img
                                            src={c.cover_image_url}
                                            alt={c.name}
                                            className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-[1200ms] ease-out"
                                            loading="lazy"
                                        />
                                    )}
                                </div>
                                <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/10 to-transparent" />
                                <div className="absolute bottom-0 left-0 right-0 p-6 md:p-7 text-white">
                                    <p className="text-[10px] tracking-[0.4em] uppercase opacity-85 mb-2">Portfolio</p>
                                    <h3 className="font-display text-2xl md:text-3xl">{c.name}</h3>
                                    {c.description && (
                                        <p className="text-xs md:text-sm text-white/85 mt-2 line-clamp-2 max-w-xs">{c.description}</p>
                                    )}
                                    <span className="inline-flex items-center gap-2 mt-4 text-[10px] tracking-[0.3em] uppercase text-blush opacity-0 -translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300">
                                        View portfolio <ArrowRight size={12} />
                                    </span>
                                </div>
                            </Link>
                        ))}
                    </div>

                    <div className="text-center mt-12">
                        <Link to="/gallery" className="btn-pink" data-testid="home-view-all-cakes">
                            View all categories <ArrowRight size={14} />
                        </Link>
                    </div>
                </div>
            </section>
        </main>
    );
}
